import type { ReactNode } from 'react';
import type { ProductLine } from '@popcart/core/v1';
import { StatusPill, type Tone } from '../ui/bits';
import './shop.css';

/**
 * A queue status sticker. NEW is the §14 Pop Yellow sticker with an Ink label, tilted 12° like the
 * card's "+ NEW"; every other tone is the shared status pill.
 */
export function StatusSticker({ tone, children }: { tone: Tone | 'new'; children: ReactNode }) {
  if (tone === 'new') return <span className="pill sticker ph-new">{children}</span>;
  return <StatusPill tone={tone}>{children}</StatusPill>;
}

/** A wait in minutes, short enough to read across the room: "12 min", "3h 05m", "6d". */
export function waitText(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return '—';
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(m / 1440)}d`;
}

const LINE_NAME: Record<ProductLine, string> = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' };

/** The order's lines as category dots (the name rides along for screen readers and hover). */
export function LineDots({ lines }: { lines: ProductLine[] }) {
  const names = lines.map((l) => LINE_NAME[l]).join(', ');
  return (
    <span className="ph-dots" title={names}>
      <span className="sr-only">{names}</span>
      {lines.map((l) => (
        <span key={l} className={`dot dot-${l}`} aria-hidden="true" />
      ))}
    </span>
  );
}
